import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../components/ui';
import { Button } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = ['new', 'in_progress', 'resolved', 'closed'] as const;

export default function SupportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    adminBackofficeApi
      .getSupportRequest(id)
      .then((data) => {
        if (data.success && data.data) {
          setRequest(data.data);
          setStatus(data.data.status || '');
          setAdminNotes(data.data.adminNotes || '');
        } else setError(data.error || 'Not found');
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const payload: { status?: string; adminNotes?: string; resolvedAt?: string } = {};
      if (status) payload.status = status;
      if (adminNotes !== (request?.adminNotes ?? '')) payload.adminNotes = adminNotes;
      if (status === 'resolved' || status === 'closed') payload.resolvedAt = new Date().toISOString();
      const res = await adminBackofficeApi.updateSupportRequest(id, payload);
      if (res.success && res.data) {
        setRequest(res.data);
        toast.success('Updated');
      } else toast.error(res.error || 'Update failed');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !request) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Support request: ${request?.id}`}
        breadcrumbs={[{ label: 'Support', href: '/backoffice/support' }]}
        help={<p>View details and update status for this support ticket.</p>}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <h2 className="text-lg font-medium">Details</h2>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><span className="text-gray-500">Subject:</span> {request?.subject}</p>
            <p><span className="text-gray-500">Type:</span> {request?.type}</p>
            <p><span className="text-gray-500">Status:</span> {request?.status}</p>
            <p><span className="text-gray-500">Message:</span></p>
            <p className="whitespace-pre-wrap">{request?.message}</p>
            {request?.userSummary && (
              <p><span className="text-gray-500">User:</span> {request.userSummary.firstName} {request.userSummary.lastName} ({request.userSummary.email})</p>
            )}
            {request?.academyName && <p><span className="text-gray-500">Academy:</span> {request.academyName}</p>}
            <p><span className="text-gray-500">Created:</span> {request?.createdAt ? new Date(request.createdAt).toLocaleString() : '-'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-medium">Admin update</h2>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Admin notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                rows={4}
                placeholder="Internal notes"
              />
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
